import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDebounce } from './useDebounce';

export interface IUseLazyLoadOptions<T> {
  pageData: T[] | undefined;
  hasMore: boolean;
  isLoading?: boolean;
  resetKey?: unknown;
  isEnabled?: boolean;
  onLoadMore: () => void;
}

export interface IUseLazyLoadReturn<T> {
  data: T[];
  page: number;
  loadMore: () => void;
  reset: () => void;
  onPopupScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

const useLazyLoad = <T>({
  isEnabled = true,
  onLoadMore,
  pageData: pageDataInitial,
  hasMore,
  isLoading = false,
  resetKey,
}: IUseLazyLoadOptions<T>): IUseLazyLoadReturn<T> => {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const lastPageDataRef = useRef<T[] | undefined>(undefined);
  const resetKeyRef = useRef(resetKey);
  const [shouldReset, setShouldReset] = useState<boolean>(false);

  const pageData = useMemo(() => {
    if (pageDataInitial === undefined || pageDataInitial === null) return [];
    return Array.isArray(pageDataInitial)
      ? pageDataInitial
      : ([pageDataInitial] as T[]);
  }, [pageDataInitial]);

  // Use this because cashe data from requect query fails to trigger second event
  // with appropriate state
  const debouncedPageData = useDebounce(pageData, 10);

  useEffect(() => {
    if (!isEnabled) return;
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    lastPageDataRef.current = undefined;
    setPage(1);
    setShouldReset(true);
  }, [resetKey, isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEnabled) return;
    if (
      pageData === undefined ||
      pageData === null ||
      pageData === lastPageDataRef.current
    )
      return;
    lastPageDataRef.current = pageData;
    setData(prev =>
      page === 1 || shouldReset ? [...pageData] : [...prev, ...pageData]
    );
    setShouldReset(false);
  }, [debouncedPageData, isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(() => {
    if (isEnabled && hasMore && !isLoading) {
      setPage(p => p + 1);
      onLoadMore();
    }
  }, [hasMore, isLoading, isEnabled]);

  const reset = useCallback(() => {
    if (!isEnabled) return;
    lastPageDataRef.current = undefined;
    setPage(1);
    setData([]);
  }, [isEnabled]);

  const scrollDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isEnabled) return;
    return () => {
      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    };
  }, [isEnabled]);

  const onPopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!isEnabled) return;
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop > clientHeight + 20) return;

      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = setTimeout(loadMore, 200);
    },
    [loadMore, isEnabled]
  );

  return { data, page, loadMore, reset, onPopupScroll };
};

export default useLazyLoad;
